import { Component, Input, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { IonicModule } from "@ionic/angular";
import { TranslateModule } from "@ngx-translate/core";
import { RegistrationField } from "../../entities/RegistrationField";

@Component({
    standalone: true,
    imports: [CommonModule, IonicModule, TranslateModule],
    selector: "attend-modal",
    templateUrl: "./attend-modal.component.html",
    styleUrls: ["./attend-modal.component.scss"]
})
export class AttendModalComponent implements OnInit {
    public numberOfParticipants: number = 1;

    @Input() callback: any;
    @Input() isAttending: boolean;
    @Input() isWithPartner: boolean;
    @Input() fields: RegistrationField[] = [];

    public values: { [key: string]: any } = {};

    ngOnInit(): void {
        // Only number fields get a default. Option fields stay unset so the user
        // must make an explicit choice (isValid enforces this).
        for (const field of this.fields) {
            if (field.type === "number") {
                this.values[field.key] = field.min ?? 0;
            }
        }
    }

    get hasFields(): boolean {
        return this.fields && this.fields.length > 0;
    }

    get participantTotal(): number {
        return this.fields
            .filter(
                (field) => field.type === "number" && field.countsAsParticipant
            )
            .reduce((total, field) => total + (this.values[field.key] || 0), 0);
    }

    get participantLimit(): number | null {
        for (const field of this.fields) {
            if (field.type !== "option") {
                continue;
            }
            const option = field.options?.find(
                (o) => o.value === this.values[field.key]
            );
            if (option?.maxParticipants != null) {
                return option.maxParticipants;
            }
        }
        return null;
    }

    get isValid(): boolean {
        if (this.participantTotal < 1) {
            return false;
        }
        const limit = this.participantLimit;
        if (limit != null && this.participantTotal > limit) {
            return false;
        }
        const numbersValid = this.fields
            .filter((field) => field.type === "number")
            .every((field) => this.values[field.key] >= (field.min ?? 0));
        const optionsChosen = this.fields
            .filter((field) => field.type === "option")
            .every((field) => this.values[field.key] != null);

        return numbersValid && optionsChosen;
    }

    selectOption(field: RegistrationField, value: string): void {
        this.values[field.key] = value;
        this.clampToLimit();
    }

    canIncrement(field: RegistrationField): boolean {
        if (field.max != null && this.values[field.key] >= field.max) {
            return false;
        }
        const limit = this.participantLimit;
        if (
            field.countsAsParticipant &&
            limit != null &&
            this.participantTotal >= limit
        ) {
            return false;
        }
        return true;
    }

    canDecrement(field: RegistrationField): boolean {
        return this.values[field.key] > (field.min ?? 0);
    }

    increment(field: RegistrationField): void {
        if (this.canIncrement(field)) {
            this.values[field.key]++;
        }
    }

    decrement(field: RegistrationField): void {
        if (this.canDecrement(field)) {
            this.values[field.key]--;
        }
    }

    toggleAttend(): void {
        if (this.hasFields && !this.isAttending) {
            if (!this.isValid) {
                return;
            }

            this.callback(true, this.participantTotal, false, {
                ...this.values
            });
            return;
        }

        this.callback(!this.isAttending, this.numberOfParticipants);
    }

    dismiss(): void {
        this.callback(this.isAttending, this.numberOfParticipants, true);
    }

    /**
     * Reduce the counting fields (from the last one) so the total fits within
     * the selected option's cap.
     */
    private clampToLimit(): void {
        const limit = this.participantLimit;
        if (limit == null) {
            return;
        }

        const countingFields = this.fields.filter(
            (field) => field.type === "number" && field.countsAsParticipant
        );

        for (
            let i = countingFields.length - 1;
            i >= 0 && this.participantTotal > limit;
            i--
        ) {
            const field = countingFields[i];
            while (
                this.participantTotal > limit &&
                this.values[field.key] > (field.min ?? 0)
            ) {
                this.values[field.key]--;
            }
        }
    }

    get attendButtonText(): string {
        let translation = "event.report-present";
        if (!this.isWithPartner) {
            return translation;
        }
        if (this.numberOfParticipants === 1) {
            translation = "event.confirm.button";
        }
        if (this.numberOfParticipants === 2) {
            translation = "event.confirm.button-multiple";
        }
        return translation;
    }
}
